import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../api";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import QRCode from "react-qr-code";
import GuestUpload from "./GuestUpload";
import Gallery from "./Gallery";
import RSVPForm from "./RSVPForm";

/* ─── Countdown ─── */
const Countdown = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const tick = () => {
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) return setTimeLeft(null);
      setTimeLeft({
        days:    Math.floor(diff / 86400000),
        hours:   Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  if (!timeLeft) return null;

  const units = [
    { label: "يوم", value: timeLeft.days },
    { label: "ساعة", value: timeLeft.hours },
    { label: "دقيقة", value: timeLeft.minutes },
    { label: "ثانية", value: timeLeft.seconds },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="countdown-strip"
    >
      <p className="countdown-label">الوقت المتبقي للمناسبة</p>
      <div className="countdown-units">
        {units.map(({ label, value }) => (
          <div className="countdown-unit" key={label}>
            <AnimatePresence mode="popLayout">
              <motion.span
                key={value}
                className="countdown-number"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {String(value).padStart(2, "0")}
              </motion.span>
            </AnimatePresence>
            <span className="countdown-unit-label">{label}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

/* ─── Reaction button ─── */
const SimpleReaction = ({ mediaId, reactions = {}, onReact }) => {
  const count = typeof reactions === 'number' ? reactions : (reactions.heart || 0);

  return (
    <div className="reaction-wrapper">
      <motion.button
        className="reaction-trigger"
        whileTap={{ scale: 0.8 }}
        onClick={(e) => { e.stopPropagation(); onReact(mediaId, "heart"); }}
      >
        ❤️ <span>{count || ""}</span>
      </motion.button>
    </div>
  );
};

/* ─── Photo card in Top Moments ─── */
const TopMomentCard = ({ item, onReact }) => (
  <motion.div
    className="top-moment-card"
    initial={{ opacity: 0, scale: 0.95 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    whileHover={{ y: -6 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    {item.type === "photo" && (
      <img src={item.url} alt="" className="top-moment-img" loading="lazy" />
    )}
    {item.type === "video" && (
      <video src={item.url} className="top-moment-img" muted />
    )}
    {item.type === "text" && (
      <div className="top-moment-text-card">
        <span className="quote-mark">"</span>
        <p>{item.content}</p>
      </div>
    )}
    <div className="top-moment-overlay">
      <span className="top-moment-guest">{item.guestName}</span>
      <SimpleReaction
        mediaId={item._id}
        reactions={item.reactions}
        onReact={onReact}
      />
    </div>
    <div className="top-moment-badge">✨ أفضل لحظة</div>
  </motion.div>
);

/* ─── Password gate ─── */
const PasswordGate = ({ onUnlock }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);

  const submit = () => {
    if (pw.trim()) { onUnlock(pw); setErr(false); }
    else setErr(true);
  };

  return (
    <div className="password-gate" dir="rtl">
      <motion.div
        className="password-card"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="lock-icon">🔒</div>
        <h2>هذا الألبوم محمي</h2>
        <p>أدخل كلمة المرور للوصول إلى صور المناسبة</p>
        <input
          type="password"
          placeholder="كلمة المرور..."
          value={pw}
          onChange={e => setPw(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submit()}
          className={`pw-input ${err ? "pw-error" : ""}`}
          dir="ltr"
        />
        {err && <p className="pw-err-msg">كلمة المرور غلط، حاول تاني</p>}
        <button className="pw-btn" onClick={submit}>دخول →</button>
      </motion.div>
    </div>
  );
};


/* ─── Leaderboard ─── */
const Leaderboard = ({ media }) => {
  const counts = {};
  media.forEach(m => {
    counts[m.guestName] = (counts[m.guestName] || 0) + 1;
  });
  const top = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  if (top.length < 2) return null;

  const medals = ["🥇", "🥈", "🥉", "④", "⑤"];

  return (
    <div className="leaderboard">
      <h3 className="section-title">🏆 أكثر المشاركين</h3>
      <div className="leaderboard-list">
        {top.map(([name, count], i) => (
          <motion.div
            key={name}
            className={`leaderboard-row ${i === 0 ? "leaderboard-first" : ""}`}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
          >
            <span className="leaderboard-medal">{medals[i]}</span>
            <div className="leaderboard-avatar">{name[0]?.toUpperCase()}</div>
            <span className="leaderboard-name">{name}</span>
            <span className="leaderboard-count">{count} مشاركة</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/* ─── Schedule ─── */
const Schedule = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="schedule-section">
      <h3 className="section-title">🗓 برنامج الحفل</h3>
      <div className="schedule-timeline">
        {items.map((item, i) => (
          <motion.div
            key={i}
            className="schedule-item"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
          >
            <div className="schedule-time">{item.time}</div>
            <div className="schedule-dot-col">
              <div className="schedule-dot" />
              {i < items.length - 1 && <div className="schedule-line" />}
            </div>
            <div className="schedule-content">
              <p className="schedule-title">{item.title}</p>
              {item.description && (
                <p className="schedule-desc">{item.description}</p>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/* ─── MAIN COMPONENT ─── */
const EventLandingPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [activeTab, setActiveTab] = useState("gallery"); // gallery | upload
  const [showCheckIn, setShowCheckIn] = useState(false);
  const uploadRef = useRef(null);

  const fetchEvent = useCallback(async () => {
    try {
      const res = await api.get(`/api/events/${eventId}/public`);
      setEvent(res.data);
      // If no password, auto-unlock
      if (!res.data.hasPassword) {
        setUnlocked(true);
      } else {
        const cached = sessionStorage.getItem(`youmak_pw_${eventId}`);
        if (cached) {
          setUnlocked(true);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [eventId]);

  const fetchMedia = useCallback(async () => {
    try {
      const res = await api.get(`/api/media/event/${eventId}?status=approved`);
      setMedia(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  useEffect(() => {
    if (unlocked) {
      fetchMedia();
      const id = setInterval(fetchMedia, 15000);
      return () => clearInterval(id);
    }
  }, [unlocked, fetchMedia]);

  // Handle RSVP Check-in Modal
  useEffect(() => {
    if (unlocked && event?.rsvpEnabled) {
      const alreadyRSVP = localStorage.getItem(`youmak_rsvp_${eventId}`);
      if (!alreadyRSVP) {
        setShowCheckIn(true);
      }
    }
  }, [unlocked, event?.rsvpEnabled, eventId]);

  const handleUnlock = async (pw) => {
    try {
      await api.post(`/api/events/${eventId}/verify-password`, { password: pw });
      sessionStorage.setItem(`youmak_pw_${eventId}`, "1");
      setUnlocked(true);
    } catch {
      // trigger error in PasswordGate
      throw new Error("wrong password");
    }
  };

  const handleReact = async (mediaId, emoji) => {
    try {
      await api.patch(`/api/media/reaction/${mediaId}`, { emoji });
      fetchMedia();
    } catch {}
  };

  // Compute top moments (top 6 by total reactions)
  const topMoments = [...media]
    .filter((m) => m.type === "photo" || m.type === "video" || m.type === "text")
    .sort((a, b) => {
      const getScore = (m) => {
        const reactions = typeof m.reactions === "number"
            ? m.reactions
            : (m.reactions?.heart || 0);
        return reactions;
      };
      return getScore(b) - getScore(a);
    })
    .slice(0, 6)
    .filter((m) => {
      const reactions = typeof m.reactions === "number"
          ? m.reactions
          : (m.reactions?.heart || 0);
      return reactions > 0;
    });

  if (!event && !loading) {
    return (
      <div className="elp-not-found" dir="rtl">
        <div className="not-found-emoji">🎭</div>
        <h2>المناسبة دي مش موجودة</h2>
        <p>تأكد من الرابط أو تواصل مع المنظم.</p>
        <Link to="/" className="elp-home-btn">الرئيسية</Link>
      </div>
    );
  }

  if (!unlocked && event?.hasPassword) {
    return <PasswordGate onUnlock={handleUnlock} />;
  }

  const eventUrl = window.location.origin + `/event/${eventId}`;
  const isFuture = event?.eventDate && new Date(event.eventDate) > new Date();

  const tabs = [
    { key: "gallery", label: "📸 الصور", count: media.length },
    { key: "upload",  label: "➕ أضف صورة" },
    ...(event?.rsvpEnabled ? [{ key: "rsvp", label: "✅ تأكيد الحضور" }] : []),
  ];

  const scheduleItems = event?.schedule || [];

  const getThemeStyles = () => {
    const themes = {
      wedding: { bg: "#fdfbf7", primary: "#8b493e", font: "'Noto Serif Arabic', serif" },
      birthday: { bg: "#fffcf0", primary: "#e67e22", font: "'Plus Jakarta Sans', sans-serif" },
      corporate: { bg: "#f4f7f6", primary: "#2c3e50", font: "'Plus Jakarta Sans', sans-serif" },
      graduation: { bg: "#f8f9fa", primary: "#2980b9", font: "'Plus Jakarta Sans', sans-serif" },
      baby_shower: { bg: "#fff5f8", primary: "#d63384", font: "'Plus Jakarta Sans', sans-serif" },
      anniversary: { bg: "#fdf2f2", primary: "#c0392b", font: "'Noto Serif Arabic', serif" },
    };
    return themes[event?.theme] || themes.wedding;
  };

  const themeStyles = getThemeStyles();

  return (
    <div className={`elp-root theme-${event?.theme || 'wedding'}`} dir="rtl">
      <style>{`
        /* ── root ── */
        .elp-root {
          min-height: 100vh;
          background: ${themeStyles.bg};
          font-family: ${themeStyles.font};
          color: #2d1e17;
        }

        /* ── hero ── */
        .elp-hero {
          position: relative;
          height: clamp(400px, 65vh, 700px);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
        }
        .elp-hero-bg {
          position: absolute; inset: 0;
          background-size: cover;
          background-position: center;
          transform: scale(1.08);
          transition: opacity 1s;
        }
        .elp-hero-bg.style-blur { filter: blur(8px) brightness(0.45); }
        .elp-hero-bg.style-dark { filter: brightness(0.3); }
        .elp-hero-bg.style-gradient { filter: saturate(1.2); }
        .elp-hero-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to bottom,
            rgba(0,0,0,0.05) 0%,
            rgba(0,0,0,0.15) 40%,
            rgba(0,0,0,0.75) 100%);
        }
        .elp-hero-content {
          position: relative; z-index: 2;
          padding: clamp(1.5rem, 4vw, 3rem);
          max-width: 900px;
          margin: 0 auto;
          width: 100%;
        }
        .elp-badge {
          display: inline-flex; align-items: center; gap: .5rem;
          background: rgba(255,255,255,.18);
          backdrop-filter: blur(8px);
          color: #fff;
          border-radius: 999px;
          padding: .3rem .9rem;
          font-size: .75rem;
          font-weight: 700;
          letter-spacing: .08em;
          text-transform: uppercase;
          margin-bottom: 1rem;
          border: 1px solid rgba(255,255,255,.25);
        }
        .elp-badge-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #4ade80;
          box-shadow: 0 0 6px #4ade80;
          animation: blink 1.5s infinite;
        }
        @keyframes blink {
          0%,100% { opacity:1; } 50% { opacity:.4; }
        }
        .elp-title {
          font-size: clamp(1.8rem, 6vw, 3.5rem);
          font-weight: 900;
          color: #fff;
          line-height: 1.15;
          margin: 0 0 .75rem;
          text-shadow: 0 2px 16px rgba(0,0,0,.4);
        }
        .elp-meta {
          display: flex; flex-wrap: wrap; gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .elp-meta-chip {
          display: flex; align-items: center; gap: .4rem;
          color: rgba(255,255,255,.85);
          font-size: .85rem; font-weight: 600;
        }
        .elp-meta-chip span { font-size: 1rem; }
        .elp-hero-cta {
          display: flex; gap: .75rem; flex-wrap: wrap;
        }
        .elp-upload-btn {
          background: ${themeStyles.primary};
          color: #fff;
          border: none;
          border-radius: 999px;
          padding: .75rem 1.75rem;
          font-size: 1rem;
          font-weight: 800;
          cursor: pointer;
          box-shadow: 0 4px 20px rgba(139,73,62,.5);
          transition: all .25s;
        }
        .elp-upload-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(139,73,62,.6); }
        .elp-scroll-btn {
          background: rgba(255,255,255,.15);
          backdrop-filter: blur(8px);
          color: #fff;
          border: 1.5px solid rgba(255,255,255,.35);
          border-radius: 999px;
          padding: .75rem 1.75rem;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all .25s;
        }
        .elp-scroll-btn:hover { background: rgba(255,255,255,.25); }

        /* ── thumbnail in hero corner ── */
        .elp-hero-portrait {
          position: absolute;
          top: 1.5rem; left: 1.5rem;
          width: 90px; height: 120px;
          border-radius: 12px;
          border: 3px solid rgba(255,255,255,.85);
          box-shadow: 0 8px 24px rgba(0,0,0,.5);
          object-fit: cover;
          z-index: 3;
        }
        @media (max-width: 576px) { .elp-hero-portrait { display: none; } }

        /* ── countdown ── */
        .countdown-strip {
          background: #fff;
          border-bottom: 1px solid #ffdad4;
          padding: 1rem;
          text-align: center;
        }
        .countdown-label {
          font-size: .75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .1em;
          color: #8b493e;
          margin: 0 0 .5rem;
        }
        .countdown-units {
          display: flex; justify-content: center; gap: 1rem;
        }
        .countdown-unit {
          display: flex; flex-direction: column; align-items: center;
          min-width: 56px;
        }
        .countdown-number {
          font-size: 2rem; font-weight: 900;
          color: #2d1e17; line-height: 1;
          display: block;
        }
        .countdown-unit-label {
          font-size: .65rem; font-weight: 700;
          color: #867370; text-transform: uppercase;
          letter-spacing: .08em;
        }

        /* ── body ── */
        .elp-body {
          max-width: 1100px;
          margin: 0 auto;
          padding: 2rem 1rem 4rem;
        }

        /* ── section title ── */
        .section-title {
          font-size: 1.25rem; font-weight: 900;
          color: #2d1e17;
          margin: 0 0 1.25rem;
          display: flex; align-items: center; gap: .5rem;
        }

        /* ── info strip ── */
        .elp-details-bg-wrapper {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          margin-bottom: 2.5rem;
          padding: 2.5rem 1.5rem;
          background-size: cover;
          background-position: center;
        }
        .elp-details-bg-overlay {
          position: absolute; inset: 0;
          background: rgba(0,0,0,0.45);
          backdrop-filter: blur(4px);
        }
        .elp-info-strip {
          position: relative; z-index: 1;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
        }
        .elp-info-card {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 18px;
          padding: 1.25rem;
          display: flex; align-items: flex-start; gap: .75rem;
          color: #fff;
        }
        .elp-info-label {
          font-size: .7rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: .08em;
          color: rgba(255,255,255,0.7); margin: 0 0 .2rem;
        }
        .elp-info-value {
          font-size: .95rem; font-weight: 700; color: #fff;
          margin: 0;
        }
        .elp-info-icon {
          font-size: 1.4rem; flex-shrink: 0;
          margin-top: .1rem;
        }

        /* ── QR share panel ── */
        .elp-qr-panel {
          background: #fff8f5;
          border: 1.5px dashed #ffb4a7;
          border-radius: 20px;
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2.5rem;
          text-align: center;
        }
        .elp-qr-title {
          font-size: 1rem; font-weight: 800; color: #8b493e;
          margin: 0;
        }
        .elp-qr-sub {
          font-size: .8rem; color: #867370; margin: 0;
        }
        .elp-qr-actions {
          display: flex; gap: .75rem; flex-wrap: wrap; justify-content: center;
        }
        .elp-copy-btn, .elp-wa-btn {
          border: none; border-radius: 999px;
          padding: .6rem 1.25rem;
          font-size: .85rem; font-weight: 700;
          cursor: pointer; transition: all .2s;
        }
        .elp-copy-btn {
          background: #8b493e; color: #fff;
        }
        .elp-copy-btn:hover { opacity: .88; }
        .elp-wa-btn {
          background: #25d366; color: #fff;
        }
        .elp-wa-btn:hover { opacity: .88; }

        /* ── tabs ── */
        .elp-tabs {
          display: flex; gap: .5rem;
          background: #f5ebe0;
          border-radius: 999px;
          padding: .35rem;
          margin-bottom: 2rem;
          width: fit-content;
          max-width: 100%;
          overflow-x: auto;
          scrollbar-width: none;
        }
        .elp-tabs::-webkit-scrollbar { display: none; }
        .elp-tab {
          border: none; background: transparent;
          border-radius: 999px;
          padding: .55rem 1.1rem;
          font-size: .875rem; font-weight: 700;
          color: #534340;
          cursor: pointer; transition: all .2s;
          white-space: nowrap;
        }
        .elp-tab.active {
          background: #fff;
          color: #8b493e;
          box-shadow: 0 2px 8px rgba(139,73,62,.12);
        }
        .elp-tab-count {
          background: #ffdad4; color: #8b493e;
          border-radius: 999px;
          padding: .05rem .45rem;
          font-size: .7rem; font-weight: 800;
          margin-right: .35rem;
        }

        /* ── top moments ── */
        .top-moments-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 0.75rem;
          margin-bottom: 2.5rem;
        }
        @media (max-width: 480px) {
          .top-moments-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.5rem;
          }
        }
        .top-moment-card {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          aspect-ratio: 4/5;
          background: #ede0d4;
          cursor: pointer;
        }
        .top-moment-img {
          width: 100%; height: 100%; object-fit: cover;
        }
        .top-moment-overlay {
          position: absolute; bottom: 0; left: 0; right: 0;
          background: linear-gradient(to top, rgba(0,0,0,.7) 0%, transparent 100%);
          padding: .75rem;
          display: flex; justify-content: space-between; align-items: flex-end;
        }
        .top-moment-guest {
          color: #fff; font-size: .8rem; font-weight: 700;
        }
        .top-moment-badge {
          position: absolute; top: .75rem; right: .75rem;
          background: rgba(255,255,255,.9);
          color: #8b493e;
          border-radius: 999px;
          padding: .2rem .6rem;
          font-size: .65rem; font-weight: 800;
          letter-spacing: .05em;
        }
        .top-moment-text-card {
          width: 100%; height: 100%;
          background: linear-gradient(135deg, #ffdad4, #ede0d4);
          display: flex; flex-direction: column;
          justify-content: center; padding: 1.5rem;
        }
        .quote-mark { font-size: 3rem; color: #8b493e; line-height: .8; }
        .top-moment-text-card p { font-size: 1rem; font-weight: 700; color: #2d1e17; }

        /* ── reactions ── */
        .reaction-wrapper { position: relative; display: inline-block; }
        .reaction-trigger {
          border: none;
          background: rgba(255,255,255,.2);
          backdrop-filter: blur(4px);
          border-radius: 999px;
          padding: .25rem .6rem;
          color: #fff;
          font-size: .85rem; font-weight: 700;
          cursor: pointer;
          display: flex; align-items: center; gap: .3rem;
        }
        .reaction-popover {
          position: absolute;
          bottom: calc(100% + .5rem);
          left: 50%; transform: translateX(-50%);
          background: #fff;
          border-radius: 999px;
          box-shadow: 0 8px 32px rgba(0,0,0,.15);
          border: 1px solid #ffdad4;
          padding: .4rem .6rem;
          display: flex; gap: .2rem;
          z-index: 100;
          white-space: nowrap;
        }
        .reaction-emoji-btn {
          border: none; background: transparent;
          font-size: 1.3rem;
          cursor: pointer;
          position: relative;
          padding: .1rem .15rem;
          border-radius: 999px;
          transition: background .15s;
        }
        .reaction-emoji-btn:hover { background: #ffdad4; }
        .reaction-count {
          position: absolute;
          top: -.2rem; right: -.3rem;
          background: #8b493e; color: #fff;
          border-radius: 999px;
          padding: 0 .3rem;
          font-size: .55rem; font-weight: 800;
        }

        /* ── schedule ── */
        .schedule-section { margin-bottom: 2.5rem; }
        .schedule-timeline { display: flex; flex-direction: column; gap: 0; }
        .schedule-item {
          display: grid;
          grid-template-columns: 64px 28px 1fr;
          gap: .5rem;
          align-items: flex-start;
        }
        .schedule-time {
          font-size: .8rem; font-weight: 800;
          color: #8b493e; text-align: center;
          padding-top: .15rem;
        }
        .schedule-dot-col { display: flex; flex-direction: column; align-items: center; }
        .schedule-dot {
          width: 12px; height: 12px;
          border-radius: 50%;
          background: #8b493e;
          border: 2px solid #fff;
          box-shadow: 0 0 0 2px #8b493e;
          flex-shrink: 0;
          margin-top: .2rem;
        }
        .schedule-line {
          width: 2px; flex: 1; min-height: 2rem;
          background: #ffdad4;
          margin: .2rem 0;
        }
        .schedule-content { padding: 0 0 1.25rem; }
        .schedule-title { font-size: .95rem; font-weight: 800; color: #2d1e17; margin: 0 0 .2rem; }
        .schedule-desc { font-size: .8rem; color: #534340; margin: 0; }

        /* ── leaderboard ── */
        .leaderboard { margin-bottom: 2.5rem; }
        .leaderboard-list { display: flex; flex-direction: column; gap: .5rem; }
        .leaderboard-row {
          display: flex; align-items: center; gap: .75rem;
          background: #fff;
          border: 1px solid #ffdad4;
          border-radius: 12px;
          padding: .75rem 1rem;
          transition: transform .2s;
        }
        .leaderboard-row:hover { transform: translateX(-4px); }
        .leaderboard-first { background: linear-gradient(135deg,#fff8f5,#ffdad4); border-color: #ffb4a7; }
        .leaderboard-medal { font-size: 1.2rem; }
        .leaderboard-avatar {
          width: 32px; height: 32px;
          border-radius: 50%;
          background: #8b493e; color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-size: .85rem; font-weight: 900;
          flex-shrink: 0;
        }
        .leaderboard-name { flex: 1; font-weight: 700; font-size: .9rem; }
        .leaderboard-count {
          font-size: .8rem; font-weight: 700;
          color: #8b493e;
          background: #ffdad4;
          border-radius: 999px;
          padding: .15rem .6rem;
        }

        /* ── RSVP ── */
        .rsvp-form {
          background: #fff;
          border: 1px solid #ffdad4;
          border-radius: 20px;
          padding: 2rem;
          text-align: center;
          max-width: 480px;
          margin: 0 auto;
        }
        .rsvp-title { font-size: 1.25rem; font-weight: 900; color: #2d1e17; margin: 0 0 .4rem; }
        .rsvp-subtitle { color: #534340; font-size: .9rem; margin: 0 0 1.5rem; }
        .rsvp-fields { display: flex; flex-direction: column; gap: .75rem; margin-bottom: 1rem; }
        .rsvp-input {
          border: 1.5px solid #ffdad4;
          border-radius: 12px;
          padding: .75rem 1rem;
          font-size: .95rem;
          width: 100%;
          outline: none;
          transition: border .2s;
          font-family: inherit;
          text-align: right;
        }
        .rsvp-input:focus { border-color: #8b493e; }
        .rsvp-input.pw-error { border-color: #ba1a1a; }
        .rsvp-btn {
          background: #8b493e; color: #fff;
          border: none; border-radius: 999px;
          padding: .85rem 2rem;
          font-size: 1rem; font-weight: 800;
          cursor: pointer; width: 100%;
          transition: all .2s;
        }
        .rsvp-btn:hover { opacity: .88; transform: translateY(-1px); }
        .rsvp-done {
          background: #dcedc8; color: #2e7d32;
          border-radius: 16px; padding: 1.5rem;
          font-weight: 700; font-size: 1rem;
          text-align: center;
          max-width: 480px; margin: 0 auto;
        }

        /* ── password gate ── */
        .password-gate {
          min-height: 100vh;
          background: #fdfbf7;
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .password-card {
          background: #fff;
          border: 1px solid #ffdad4;
          border-radius: 24px;
          padding: 2.5rem;
          text-align: center;
          max-width: 380px;
          width: 100%;
          box-shadow: 0 16px 48px rgba(139,73,62,.1);
        }
        .lock-icon { font-size: 3rem; margin-bottom: 1rem; }
        .password-card h2 { font-size: 1.4rem; font-weight: 900; color: #2d1e17; margin: 0 0 .5rem; }
        .password-card p { color: #534340; font-size: .9rem; margin: 0 0 1.5rem; }
        .pw-input {
          width: 100%; border: 1.5px solid #ffdad4;
          border-radius: 12px; padding: .75rem 1rem;
          font-size: 1rem; text-align: center;
          outline: none; margin-bottom: .75rem;
          font-family: monospace;
          transition: border .2s;
        }
        .pw-input:focus { border-color: #8b493e; }
        .pw-input.pw-error { border-color: #ba1a1a; }
        .pw-err-msg { color: #ba1a1a; font-size: .8rem; margin: 0 0 .75rem; }
        .pw-btn {
          background: #8b493e; color: #fff;
          border: none; border-radius: 999px;
          padding: .85rem; width: 100%;
          font-size: 1rem; font-weight: 800;
          cursor: pointer;
        }

        /* ── not found ── */
        .elp-not-found {
          min-height: 100vh;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          text-align: center; padding: 2rem;
        }
        .not-found-emoji { font-size: 4rem; margin-bottom: 1rem; }
        .elp-home-btn {
          margin-top: 1rem;
          background: #8b493e; color: #fff;
          border-radius: 999px; padding: .75rem 2rem;
          font-weight: 700; text-decoration: none;
        }

        /* ── sticky mobile upload btn ── */
        .elp-sticky-upload {
          position: fixed; bottom: 1.25rem; left: 50%;
          transform: translateX(-50%);
          background: #8b493e; color: #fff;
          border: none; border-radius: 999px;
          padding: .85rem 2rem;
          font-size: 1rem; font-weight: 800;
          box-shadow: 0 8px 24px rgba(139,73,62,.45);
          cursor: pointer; z-index: 200;
          display: none;
        }
        @media (max-width: 768px) { .elp-sticky-upload { display: flex; align-items: center; gap: .5rem; } }

        /* ── footer ── */
        .elp-footer {
          text-align: center;
          padding: 2rem;
          font-size: .8rem;
          color: #867370;
          border-top: 1px solid #ffdad4;
        }
        .elp-footer a { color: #8b493e; font-weight: 700; text-decoration: none; }
        .elp-rights-note {
          text-align: center;
          padding: 1rem;
          font-size: 0.75rem;
          color: #867370;
          opacity: 0.7;
          font-style: italic;
        }
      `}</style>

      {/* ── HERO ── */}
      {loading ? (
        <div style={{ height: "65vh", background: "#ede0d4", animation: "pulse 1.5s infinite" }} />
      ) : (
        <div className="elp-hero">
          <div
            className={`elp-hero-bg style-${event?.coverStyle || 'blur'}`}
            style={{ backgroundImage: event?.clientPhoto ? `url(${event.clientPhoto})` : "linear-gradient(135deg,#ffdad4,#ede0d4)" }}
          />
          <div className="elp-hero-overlay" />
          {event?.clientPhoto && (
            <img src={event.clientPhoto} alt="" className="elp-hero-portrait" />
          )}
          <div className="elp-hero-content">
            <motion.div
              className="elp-badge"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="elp-badge-dot" />
              ألبوم حي
            </motion.div>
            <motion.h1
              className="elp-title"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: .1 }}
            >
              {event?.name}
            </motion.h1>
            <motion.div
              className="elp-meta"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: .2 }}
            >
              {event?.eventDate && (
                <span className="elp-meta-chip">
                  <span className="material-symbols-outlined">calendar_month</span>
                  {new Date(event.eventDate).toLocaleDateString("ar-EG", { dateStyle: "long" })}
                </span>
              )}
              {event?.locationName && (
                <span className="elp-meta-chip">
                  <span className="material-symbols-outlined">location_on</span>
                  {event.locationName}
                </span>
              )}
              <span className="elp-meta-chip">
                <span className="material-symbols-outlined">photo_library</span>
                {media.length} صورة
              </span>
            </motion.div>
            <motion.div
              className="elp-hero-cta"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: .3 }}
            >
              <button
                className="elp-upload-btn"
                onClick={() => { setActiveTab("upload"); uploadRef.current?.scrollIntoView({ behavior: "smooth" }); }}
              >
                📸 أضف صورتك
              </button>
              <button
                className="elp-scroll-btn"
                onClick={() => navigator.clipboard.writeText(window.location.href)}
              >
                نسخ الرابط 🔗
              </button>
              <button
                className="elp-scroll-btn"
                style={{ opacity: 0.8 }}
                onClick={() => document.getElementById("gallery-section")?.scrollIntoView({ behavior: "smooth" })}
              >
                عرض الصور ↓
              </button>
            </motion.div>
          </div>
        </div>
      )}

      {/* ── COUNTDOWN ── */}
      {isFuture && <Countdown targetDate={event.eventDate} />}

      {/* ── BODY ── */}
      <div className="elp-body">

        {/* Info strip with Banner Background */}
        {event && (
          <motion.div
            className="elp-details-bg-wrapper"
            style={{ backgroundImage: event.clientPhoto ? `url(${event.clientPhoto})` : 'none', backgroundColor: !event.clientPhoto ? themeStyles.primary : 'transparent' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="elp-details-bg-overlay" />
            <div className="elp-info-strip">
              {event.description && (
                <div className="elp-info-card">
                  <span className="elp-info-icon">💬</span>
                  <div>
                    <p className="elp-info-label">عن المناسبة</p>
                    <p className="elp-info-value" style={{ fontWeight: 400, fontSize: ".85rem", lineHeight: 1.6 }}>{event.description}</p>
                  </div>
                </div>
              )}
              {event.clientName && (
                <div className="elp-info-card">
                  <span className="elp-info-icon">👑</span>
                  <div>
                    <p className="elp-info-label">صاحب المناسبة</p>
                    <p className="elp-info-value">{event.clientName}</p>
                  </div>
                </div>
              )}
              {event.dressCode && (
                <div className="elp-info-card">
                  <span className="elp-info-icon">👔</span>
                  <div>
                    <p className="elp-info-label">كود اللبس</p>
                    <p className="elp-info-value">{event.dressCode}</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Schedule */}
        {scheduleItems.length > 0 && <Schedule items={scheduleItems} />}

        {/* QR Share panel */}
        {event && (
          <motion.div
            className="elp-qr-panel"
            initial={{ opacity: 0, scale: .97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div>
              <p className="elp-qr-title">📲 شارك مع الضيوف</p>
              <p className="elp-qr-sub">امسح الكود أو انسخ الرابط</p>
            </div>
            <QRCode value={eventUrl} size={100} />
            <div className="elp-qr-actions">
              <button
                className="elp-copy-btn"
                onClick={() => navigator.clipboard.writeText(eventUrl)}
              >
                نسخ الرابط 🔗
              </button>
              <button
                className="elp-wa-btn"
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent("شاركنا أجمل اللحظات في: " + eventUrl)}`)}
              >
                واتساب 💬
              </button>
            </div>
          </motion.div>
        )}

        {/* Tabs */}
        <div ref={uploadRef}>
          <div className="elp-tabs">
            {tabs.map(t => (
              <button
                key={t.key}
                className={`elp-tab ${activeTab === t.key ? "active" : ""}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.count !== undefined && <span className="elp-tab-count">{t.count}</span>}
                {t.label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {activeTab === "gallery" && (
              <motion.div
                key="gallery"
                id="gallery-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Top Moments */}
                {topMoments.length > 0 && (
                  <div style={{ marginBottom: "2.5rem" }}>
                    <h3 className="section-title">✨ أفضل اللحظات</h3>
                    <div className="top-moments-grid">
                      {topMoments.map(item => (
                        <TopMomentCard
                          key={item._id}
                          item={item}
                          onReact={handleReact}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Full gallery */}
                {media.length > 0 && (
                  <div>
                    <h3 className="section-title">📸 كل الصور ({media.length})</h3>
                    <Gallery
                      images={media}
                      onLike={fetchMedia}
                      scrollToUpload={() => { setActiveTab("upload"); uploadRef.current?.scrollIntoView({ behavior: "smooth" }); }}
                    />
                  </div>
                )}

                {/* Leaderboard */}
                <Leaderboard media={media} />
              </motion.div>
            )}

            {activeTab === "upload" && (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                <GuestUpload eventId={eventId} onUpload={fetchMedia} eventData={event} />
              </motion.div>
            )}

            {activeTab === "rsvp" && (
              <motion.div
                key="rsvp"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                <RSVPForm eventId={eventId} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Sticky mobile upload button */}
      {activeTab === "gallery" && (
        <button
          className="elp-sticky-upload"
          onClick={() => { setActiveTab("upload"); uploadRef.current?.scrollIntoView({ behavior: "smooth" }); }}
        >
          📸 أضف صورتك
        </button>
      )}

      {/* Social Proof & Rights Note (Guestpix inspired) */}
      <div className="elp-rights-note">
        <p>جميع الحقوق محفوظة للمنظم والضيوف. نحن لا نمتلك محتواك. ✨</p>
      </div>

      {/* Footer */}
      <div className="elp-footer">
        صُنع بكل حب بواسطة <a href="/">يومك</a> — المنصة الأولى لتوثيق المناسبات في مصر
      </div>

      {/* Check-in / RSVP Prompt Modal */}
      <AnimatePresence>
        {showCheckIn && (
          <div className="checkin-overlay">
            <motion.div
              className="checkin-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="checkin-header">
                <span className="checkin-emoji">👋</span>
                <h3>أهلاً بك في {event?.name}</h3>
                <p>يسعدنا انضمامك! هل يمكنك تأكيد حضورك في قائمة الضيوف؟</p>
              </div>

              <RSVPForm
                eventId={eventId}
                onSuccess={() => setTimeout(() => setShowCheckIn(false), 2000)}
              />

              <button
                className="checkin-skip-btn"
                onClick={() => setShowCheckIn(false)}
              >
                تخطي والدخول للألبوم ←
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>{`
        .checkin-overlay {
          position: fixed; inset: 0; background: rgba(139, 73, 62, 0.4);
          backdrop-filter: blur(8px); z-index: 2000;
          display: flex; align-items: center; justify-content: center;
          padding: 1.5rem;
        }
        .checkin-modal {
          background: #fff; border-radius: 32px; width: 100%; max-width: 500px;
          padding: 2rem; box-shadow: 0 20px 60px rgba(0,0,0,0.2);
          max-height: 90vh; overflow-y: auto;
        }
        .checkin-header { text-align: center; margin-bottom: 2rem; }
        .checkin-emoji { font-size: 3rem; display: block; margin-bottom: 1rem; }
        .checkin-header h3 { font-size: 1.4rem; font-weight: 900; color: #2d1e17; margin-bottom: .5rem; }
        .checkin-header p { color: #867370; font-size: .95rem; line-height: 1.5; margin: 0; }
        .checkin-skip-btn {
          width: 100%; border: none; background: transparent;
          color: #8b493e; font-weight: 700; font-size: .9rem;
          margin-top: 1.5rem; cursor: pointer; opacity: .7;
          transition: opacity .2s;
        }
        .checkin-skip-btn:hover { opacity: 1; text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default EventLandingPage;
